import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Play, CheckCircle2, ArrowRight, Heart, Sparkles, Users, BookOpen } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
const steps = [{
  number: '01',
  title: 'Discover Your Path',
  description: 'Explore our introductory courses and find the practice that resonates with your spiritual journey.',
  icon: Sparkles
}, {
  number: '02',
  title: 'Learn the Foundations',
  description: 'Master essential meditation techniques, breathing practices, and mindfulness principles.',
  icon: BookOpen
}, {
  number: '03',
  title: 'Join Our Community',
  description: 'Connect with fellow seekers, attend group sessions, and receive guidance from experienced teachers.',
  icon: Users
}, {
  number: '04',
  title: 'Deepen Your Practice',
  description: 'Progress through advanced teachings, attend retreats, and experience profound transformation.',
  icon: Heart
}];
const benefits = ['Reduce stress and anxiety naturally', 'Improve focus and mental clarity', 'Develop emotional resilience', 'Experience inner peace and contentment', 'Connect with your authentic self', 'Build lasting spiritual community', 'Access lifetime learning resources', 'Receive personalized guidance'];
const testimonials = [{
  name: 'Sarah Mitchell',
  role: 'Completed Foundation Course',
  image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?q=80&w=1887&auto=format&fit=crop',
  text: 'Starting my journey here was the best decision I ever made. The teachings are profound yet accessible, and the community is incredibly supportive.'
}, {
  name: 'David Chen',
  role: 'Long-term Practitioner',
  image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?q=80&w=1887&auto=format&fit=crop',
  text: "The transformation I've experienced is beyond words. This center provides authentic teachings that truly work in modern life."
}, {
  name: 'Priya Sharma',
  role: 'Retreat Participant',
  image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?q=80&w=2070&auto=format&fit=crop',
  text: 'Every course deepens my practice. The teachers are genuine, the environment is peaceful, and the results are life-changing.'
}];
export function StartJourneyPage() {
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  return <div className="min-h-screen bg-[#F5FAFD]" data-id="element-870">
      <Header data-id="element-871" />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden" data-id="element-872">
        <div className="absolute inset-0 bg-gradient-to-br from-[#5B9BD5]/10 via-[#C5E0F4]/5 to-transparent" data-id="element-873" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-874">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} className="text-center max-w-4xl mx-auto mb-16" data-id="element-875">
            <span className="inline-block px-4 py-2 rounded-full bg-[#5B9BD5]/10 text-[#5B9BD5] font-medium text-sm uppercase tracking-wider mb-6" data-id="element-876">
              Begin Your Transformation
            </span>
            <h1 className="font-serif text-5xl md:text-7xl text-[#3A5F7D] mb-6 leading-tight" data-id="element-877">
              Start Your Journey to
              <br data-id="element-878" />
              <span className="text-[#5B9BD5] italic" data-id="element-879">Inner Peace</span>
            </h1>
            <p className="text-xl text-[#6B8FA8] leading-relaxed mb-10" data-id="element-880">
              Discover authentic meditation practices that transform your life.
              Join thousands who have found peace, clarity, and purpose through
              our time-tested teachings.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-881">
              <button className="bg-[#5B9BD5] hover:bg-[#4A7FB8] text-white px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300 transform hover:scale-105 shadow-lg" data-id="element-882">
                Enroll in Foundation Course
              </button>
              <button className="border-2 border-[#5B9BD5] text-[#5B9BD5] hover:bg-[#5B9BD5] hover:text-white px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300" data-id="element-883">
                Schedule a Visit
              </button>
            </div>
          </motion.div>

          {/* Video Section */}
          <motion.div initial={{
          opacity: 0,
          y: 40
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.3
        }} className="relative max-w-5xl mx-auto" data-id="element-884">
            <div className="relative aspect-video rounded-3xl overflow-hidden shadow-2xl group cursor-pointer" onClick={() => setIsVideoPlaying(true)} data-id="element-885">
              <img src="https://images.unsplash.com/photo-1506126613408-eca07ce68773?q=80&w=2070&auto=format&fit=crop" alt="Introduction to meditation" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" data-id="element-886" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/30 to-transparent group-hover:from-black/70 transition-colors" data-id="element-887" />

              {!isVideoPlaying && <div className="absolute inset-0 flex items-center justify-center" data-id="element-888">
                  <div className="w-20 h-20 rounded-full bg-white/95 backdrop-blur-sm flex items-center justify-center transform group-hover:scale-110 transition-transform shadow-2xl" data-id="element-889">
                    <Play className="w-10 h-10 text-[#5B9BD5] fill-current ml-1" data-id="element-890" />
                  </div>
                </div>}

              <div className="absolute bottom-8 left-8 right-8" data-id="element-891">
                <h3 className="font-serif text-3xl text-white mb-2" data-id="element-892">
                  Welcome to Supradhyan
                </h3>
                <p className="text-white/90 text-lg" data-id="element-893">
                  Watch Tatwagyani Sumanand Sagar introduce our center and
                  teachings
                </p>
              </div>
            </div>

            {/* Video Stats */}
            <div className="grid grid-cols-3 gap-6 mt-8" data-id="element-894">
              <div className="text-center" data-id="element-895">
                <div className="font-serif text-4xl font-bold text-[#5B9BD5] mb-2" data-id="element-896">
                  10K+
                </div>
                <div className="text-[#6B8FA8]" data-id="element-897">Students Transformed</div>
              </div>
              <div className="text-center" data-id="element-898">
                <div className="font-serif text-4xl font-bold text-[#5B9BD5] mb-2" data-id="element-899">
                  40+
                </div>
                <div className="text-[#6B8FA8]" data-id="element-900">Years of Teaching</div>
              </div>
              <div className="text-center" data-id="element-901">
                <div className="font-serif text-4xl font-bold text-[#5B9BD5] mb-2" data-id="element-902">
                  500+
                </div>
                <div className="text-[#6B8FA8]" data-id="element-903">Courses Offered</div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Your Path Section */}
      <section className="py-20 bg-white" data-id="element-904">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-905">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-906">
            <div className="text-center mb-16" data-id="element-907">
              <h2 className="font-serif text-4xl md:text-5xl text-[#3A5F7D] mb-4" data-id="element-908">
                Your Path to Enlightenment
              </h2>
              <p className="text-lg text-[#6B8FA8] max-w-2xl mx-auto" data-id="element-909">
                A clear, structured approach to spiritual growth designed for
                modern seekers
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-910">
              {steps.map((step, index) => <motion.div key={index} initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: index * 0.1
            }} className="relative" data-id="element-911">
                  <div className="bg-[#F5FAFD] p-8 rounded-2xl h-full border-2 border-transparent hover:border-[#5B9BD5]/30 transition-all duration-300" data-id="element-912">
                    <div className="w-16 h-16 rounded-full bg-[#5B9BD5]/10 flex items-center justify-center mb-6" data-id="element-913">
                      <step.icon className="w-8 h-8 text-[#5B9BD5]" data-id="element-914" />
                    </div>
                    <div className="font-serif text-5xl font-bold text-[#5B9BD5]/20 mb-4" data-id="element-915">
                      {step.number}
                    </div>
                    <h3 className="font-serif text-2xl text-[#3A5F7D] mb-3" data-id="element-916">
                      {step.title}
                    </h3>
                    <p className="text-[#6B8FA8] leading-relaxed" data-id="element-917">
                      {step.description}
                    </p>
                  </div>

                  {index < steps.length - 1 && <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10" data-id="element-918">
                      <ArrowRight className="w-8 h-8 text-[#5B9BD5]/30" data-id="element-919" />
                    </div>}
                </motion.div>)}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-[#F5FAFD]" data-id="element-920">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-921">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-922">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} data-id="element-923">
              <h2 className="font-serif text-4xl md:text-5xl text-[#3A5F7D] mb-6" data-id="element-924">
                What You'll Experience
              </h2>
              <p className="text-lg text-[#6B8FA8] mb-8 leading-relaxed" data-id="element-925">
                Our comprehensive approach to meditation and spiritual growth
                brings profound, lasting transformation to every aspect of your
                life.
              </p>

              <div className="space-y-4" data-id="element-926">
                {benefits.map((benefit, index) => <motion.div key={index} initial={{
                opacity: 0,
                x: -20
              }} whileInView={{
                opacity: 1,
                x: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.4,
                delay: index * 0.05
              }} className="flex items-start gap-3" data-id="element-927">
                    <CheckCircle2 className="w-6 h-6 text-[#5B9BD5] flex-shrink-0 mt-0.5" data-id="element-928" />
                    <span className="text-[#3A5F7D] font-medium" data-id="element-929">
                      {benefit}
                    </span>
                  </motion.div>)}
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="relative" data-id="element-930">
              <div className="grid grid-cols-2 gap-4" data-id="element-931">
                <img src="https://images.unsplash.com/photo-1544367563-12123d8965cd?q=80&w=2070&auto=format&fit=crop" alt="Meditation practice" className="rounded-2xl shadow-lg w-full h-64 object-cover" data-id="element-932" />
                <img src="https://images.unsplash.com/photo-1593811167562-9cef47bfc4d7?q=80&w=2072&auto=format&fit=crop" alt="Peaceful environment" className="rounded-2xl shadow-lg w-full h-64 object-cover mt-8" data-id="element-933" />
              </div>
              <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-[#5B9BD5]/20 rounded-full blur-3xl -z-10" data-id="element-934" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-white" data-id="element-935">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-936">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-937">
            <div className="text-center mb-16" data-id="element-938">
              <h2 className="font-serif text-4xl md:text-5xl text-[#3A5F7D] mb-4" data-id="element-939">
                Stories of Transformation
              </h2>
              <p className="text-lg text-[#6B8FA8] max-w-2xl mx-auto" data-id="element-940">
                Hear from those who have walked this path before you
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-941">
              {testimonials.map((testimonial, index) => <motion.div key={index} initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: index * 0.1
            }} className="bg-[#F5FAFD] p-8 rounded-2xl" data-id="element-942">
                  <p className="text-[#6B8FA8] mb-6 italic leading-relaxed" data-id="element-943">
                    "{testimonial.text}"
                  </p>
                  <div className="flex items-center gap-4" data-id="element-944">
                    <img src={testimonial.image} alt={testimonial.name} className="w-14 h-14 rounded-full object-cover" data-id="element-945" />
                    <div data-id="element-946">
                      <div className="font-semibold text-[#3A5F7D]" data-id="element-947">
                        {testimonial.name}
                      </div>
                      <div className="text-sm text-[#6B8FA8]" data-id="element-948">
                        {testimonial.role}
                      </div>
                    </div>
                  </div>
                </motion.div>)}
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-[#5B9BD5] to-[#4A7FB8] text-white relative overflow-hidden" data-id="element-949">
        <div className="absolute inset-0 opacity-10" data-id="element-950">
          <div className="absolute top-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl" data-id="element-951" />
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl" data-id="element-952" />
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10" data-id="element-953">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-954">
            <h2 className="font-serif text-4xl md:text-5xl mb-6" data-id="element-955">
              Ready to Begin?
            </h2>
            <p className="text-xl mb-10 text-white/90 max-w-2xl mx-auto" data-id="element-956">
              Take the first step on your journey to inner peace. Our foundation
              course starts next month with limited spaces available.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-957">
              <button className="bg-white text-[#5B9BD5] hover:bg-[#F5FAFD] px-10 py-4 rounded-full font-semibold text-lg transition-all duration-300 transform hover:scale-105 shadow-xl" data-id="element-958">
                Enroll Now
              </button>
              <button className="border-2 border-white text-white hover:bg-white/10 px-10 py-4 rounded-full font-semibold text-lg transition-all duration-300" data-id="element-959">
                Contact Us
              </button>
            </div>
            <p className="mt-8 text-sm text-white/70" data-id="element-960">
              Questions? Call us at +91 98765 43210 or email
              contact@supradhyan.org
            </p>
          </motion.div>
        </div>
      </section>

      <Footer data-id="element-961" />
    </div>;
}